import {
  PropertyRelation,
  PropertyUserRelation,
} from "../../models/property-model";
import { RoleRelation, Roles } from "../../models/role-model";
import { User } from "../../models/user-model";
import { ConfigPropertyRelation } from "../../models/config-property-model";
import { getApiErrorMessage, getHttpStatus } from "./utils/apiError";

export interface AuthSession {
  jwt: string;
  user: User;
}

export interface UserPropertyOption {
  id: number;
  documentId?: string;
  name: string;
  address?: string;
  type?: string;
}

export function canAccessPropertySetup(
  role: RoleRelation | Roles | null | undefined,
): boolean {
  if (!role) {
    return false;
  }

  const type = (role.type ?? "").toLowerCase();
  if (type === "internal_operation" || type.includes("admin")) {
    return true;
  }

  const name = (role.name ?? "").toLowerCase();
  return name.includes("admin");
}

function toPropertyOption(
  property: PropertyUserRelation | PropertyRelation | null | undefined,
): UserPropertyOption | null {
  if (!property?.id) {
    return null;
  }

  return {
    id: property.id,
    documentId: "documentId" in property ? property.documentId : undefined,
    name: property.name?.trim() || `Propiedad ${property.id}`,
    address: property.address,
    type: property.type,
  };
}

export function collectUserProperties(
  user: User,
  linkedProperties: PropertyRelation[] = [],
  configProperties: ConfigPropertyRelation[] = [],
): UserPropertyOption[] {
  const byId = new Map<number, UserPropertyOption>();

  const add = (
    property: PropertyUserRelation | PropertyRelation | null | undefined,
  ) => {
    const option = toPropertyOption(property);
    if (option) {
      byId.set(option.id, option);
    }
  };

  add(user.property);

  const companyProperties = (
    user.company as { properties?: PropertyRelation[] } | undefined
  )?.properties;
  companyProperties?.forEach(add);
  linkedProperties.forEach(add);
  configProperties.forEach((configProperty) => add(configProperty.property));

  return Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "es"),
  );
}

export function getAuthErrorMessage(error: unknown): string {
  const status = getHttpStatus(error);

  if (status === 403) {
    return "No tienes permiso para acceder al setup de propiedades. Usa un usuario con rol de administración o internal_operation.";
  }

  if (status === 400 || status === 401) {
    return "Correo o contraseña incorrectos. Verifica tus datos e intenta de nuevo.";
  }

  return getApiErrorMessage(
    error,
    "No se pudo iniciar sesión. Verifica tu correo y contraseña.",
  );
}
